import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';
import type {
  BulkPriceChange,
  BulkPriceResult,
  BulkPriceUpdateInput,
  PriceImportResult,
} from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Input, Select } from '@/components/ui/Field.js';
import { DataTable } from '@/components/ui/DataTable.js';
import { Badge } from '@/components/ui/Badge.js';
import { useToast } from '@/components/ui/Toast.js';
import { queryKeys } from '@/lib/query-keys.js';
import { errorMessage } from '@/lib/api-error.js';
import { dateTime, price as formatPrice } from '@/lib/format.js';
import { MultiSelect } from '@/features/blocks/BlockFields.js';
import {
  brandsApi,
  categoriesApi,
  pricesApi,
  productsApi,
  type PriceHistoryRow,
} from './shop-api.js';

const OPERATIONS = [
  { value: 'percent' as const, label: 'Foizga o‘zgartirish' },
  { value: 'absolute' as const, label: 'Summa qo‘shish / ayirish' },
  { value: 'set' as const, label: 'Aniq narx qo‘yish' },
];

const SCOPES = [
  { value: 'all' as const, label: 'Butun katalog' },
  { value: 'category' as const, label: 'Kategoriya bo‘yicha' },
  { value: 'brand' as const, label: 'Brend bo‘yicha' },
  { value: 'products' as const, label: 'Tanlangan mahsulotlar' },
];

const SOURCE_LABELS: Record<PriceHistoryRow['source'], string> = {
  manual: 'Qo‘lda',
  bulk: 'Ommaviy',
  import: 'Import',
  migration: 'Migratsiya',
};

/**
 * Narxlar ekrani (TZ 7.1).
 *
 * Uch qism: ommaviy yangilash, XLSX import/eksport va o'zgarishlar tarixi.
 * Ommaviy yangilash MAJBURIY ravishda ikki qadamli: avval `dryRun` bilan
 * nima o'zgarishi ko'rsatiladi, keyin xodim tasdiqlaydi. Bir bosishda
 * 500 ta narxni o'zgartirib yuborish qaytarib bo'lmaydigan xato bo'lardi.
 */
export function PricesPage() {
  return (
    <div className="space-y-4 p-4 sm:p-6">
      <h1 className="text-xl font-semibold text-n-900">Narxlar</h1>

      <div className="grid gap-4 xl:grid-cols-2">
        <BulkUpdateCard />
        <ImportExportCard />
      </div>

      <HistoryCard />
    </div>
  );
}

// ─── Ommaviy yangilash ───────────────────────────────────────────────────────

function BulkUpdateCard() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [operation, setOperation] = useState<BulkPriceUpdateInput['operation']>('percent');
  const [scope, setScope] = useState<BulkPriceUpdateInput['scope']>('all');
  const [value, setValue] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [productIds, setProductIds] = useState<string[]>([]);
  const [roundTo, setRoundTo] = useState('1000');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<BulkPriceResult | null>(null);

  const listParams = { limit: 100, sort: 'title' as const };
  const categories = useQuery({
    queryKey: queryKeys.content.list('categories', { ...listParams, purpose: 'prices' }),
    queryFn: ({ signal }) => categoriesApi.list(listParams, signal),
    enabled: scope === 'category',
    staleTime: 5 * 60 * 1000,
  });
  const brands = useQuery({
    queryKey: queryKeys.content.list('brands', { ...listParams, purpose: 'prices' }),
    queryFn: ({ signal }) => brandsApi.list(listParams, signal),
    enabled: scope === 'brand',
    staleTime: 5 * 60 * 1000,
  });
  const products = useQuery({
    queryKey: queryKeys.content.list('products', { ...listParams, purpose: 'prices' }),
    queryFn: ({ signal }) => productsApi.list(listParams, signal),
    enabled: scope === 'products',
    staleTime: 5 * 60 * 1000,
  });

  /** Ikkala qadam ham AYNAN shu kirishni yuboradi — faqat `dryRun` farq qiladi. */
  function buildInput(dryRun: boolean): BulkPriceUpdateInput {
    return {
      operation,
      value: Number(value),
      scope,
      ...(scope === 'category' ? { categoryId } : {}),
      ...(scope === 'brand' ? { brandId } : {}),
      ...(scope === 'products' ? { productIds } : {}),
      roundTo: Number(roundTo) || 1,
      note,
      dryRun,
    } as BulkPriceUpdateInput;
  }

  const run = useMutation({
    mutationFn: (dryRun: boolean) => pricesApi.bulk(buildInput(dryRun)),
    onSuccess: async (result) => {
      if (result.dryRun) {
        setPreview(result);
        if (result.changes.length === 0) toast.info('Shartlarga mos narx topilmadi');
        return;
      }

      setPreview(null);
      toast.success(`${result.changes.length} ta narx yangilandi`);
      await queryClient.invalidateQueries({ queryKey: ['prices'] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.content.all('products') });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const canRun = value !== '' && !Number.isNaN(Number(value));

  return (
    <Card>
      <CardHeader
        title="Ommaviy yangilash"
        description="Avval o‘zgarishlar ro‘yxati ko‘rsatiladi, keyin tasdiqlaysiz"
      />
      <CardBody className="space-y-4">
        <Select
          label="Amal"
          value={operation}
          options={OPERATIONS}
          onChange={(event) => {
            setOperation(event.target.value as BulkPriceUpdateInput['operation']);
            setPreview(null);
          }}
        />

        <Input
          type="number"
          label={operation === 'percent' ? 'Foiz' : 'Summa (so‘m)'}
          value={value}
          placeholder={operation === 'percent' ? '10 yoki -15' : '500000'}
          onChange={(event) => {
            setValue(event.target.value);
            setPreview(null);
          }}
          hint={
            operation === 'set'
              ? 'Barcha mos komplektatsiyalarga shu narx qo‘yiladi'
              : 'Manfiy son — kamaytirish. «Narxni so‘rang» pozitsiyalarga tegilmaydi'
          }
        />

        <Select
          label="Qamrov"
          value={scope}
          options={SCOPES}
          onChange={(event) => {
            setScope(event.target.value as BulkPriceUpdateInput['scope']);
            setPreview(null);
          }}
        />

        {scope === 'category' && (
          <Select
            label="Kategoriya"
            value={categoryId}
            placeholder="Tanlang"
            options={(categories.data?.data ?? []).map((row) => ({
              value: row._id,
              label: row.name?.uz || row.slug || row._id,
            }))}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setPreview(null);
            }}
          />
        )}

        {scope === 'brand' && (
          <Select
            label="Brend"
            value={brandId}
            placeholder="Tanlang"
            options={(brands.data?.data ?? []).map((row) => ({
              value: row._id,
              label: row.name || row.slug || row._id,
            }))}
            onChange={(event) => {
              setBrandId(event.target.value);
              setPreview(null);
            }}
          />
        )}

        {scope === 'products' && (
          <MultiSelect
            label="Mahsulotlar"
            max={500}
            selected={productIds}
            options={(products.data?.data ?? []).map((row) => ({
              value: row._id,
              label: row.title?.uz || row.slug || row._id,
            }))}
            onChange={(next) => {
              setProductIds(next);
              setPreview(null);
            }}
          />
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            type="number"
            label="Yaxlitlash"
            min={1}
            value={roundTo}
            onChange={(event) => setRoundTo(event.target.value)}
            hint="1000 — eng yaqin mingga"
          />
          <Input
            label="Izoh"
            value={note}
            placeholder="Kurs o‘zgarishi"
            onChange={(event) => setNote(event.target.value)}
            hint="Tarixda ko‘rinadi"
          />
        </div>

        {preview === null ? (
          <Button loading={run.isPending} disabled={!canRun} onClick={() => run.mutate(true)}>
            Oldindan ko‘rish
          </Button>
        ) : (
          <BulkPreview
            result={preview}
            loading={run.isPending}
            onConfirm={() => run.mutate(false)}
            onCancel={() => setPreview(null)}
          />
        )}
      </CardBody>
    </Card>
  );
}

function BulkPreview({
  result,
  loading,
  onConfirm,
  onCancel,
}: {
  result: BulkPriceResult;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-3 border border-warning-200 bg-warning-50 p-3">
      <p className="text-sm font-medium text-n-800">
        {result.changes.length} ta komplektatsiya o‘zgaradi
        {result.skippedUnpriced > 0 && (
          <span className="font-normal text-n-600">
            {' '}
            · {result.skippedUnpriced} tasi «Narxni so‘rang» — tegilmaydi
          </span>
        )}
      </p>

      <div className="max-h-64 overflow-y-auto border border-n-200 bg-white">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-n-100">
            {result.changes.slice(0, 100).map((change: BulkPriceChange) => (
              <tr key={`${change.productId}-${change.variantId}`}>
                <td className="px-3 py-1.5 text-n-800">
                  {change.productTitle}
                  <span className="text-n-400"> · {change.variantTitle}</span>
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap text-n-600">
                  {formatPrice(change.oldPrice)}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap font-medium text-n-900">
                  → {formatPrice(change.newPrice)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {result.changes.length > 100 && (
          <p className="px-3 py-2 text-xs text-n-600">
            Ro‘yxatda birinchi 100 tasi ko‘rsatilgan — tasdiqlansa hammasi yangilanadi.
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <Button loading={loading} disabled={result.changes.length === 0} onClick={onConfirm}>
          Tasdiqlash va yangilash
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Bekor qilish
        </Button>
      </div>
    </div>
  );
}

// ─── XLSX ────────────────────────────────────────────────────────────────────

function ImportExportCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<PriceImportResult | null>(null);

  const exportFile = useMutation({
    mutationFn: () => pricesApi.exportFile(),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'narxlar.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const importFile = useMutation({
    mutationFn: (dryRun: boolean) => {
      if (!file) throw new Error('Fayl tanlanmagan');
      return pricesApi.import(file, { dryRun, note });
    },
    onSuccess: async (result) => {
      if (result.dryRun) {
        setPreview(result);
        return;
      }
      setPreview(null);
      setFile(null);
      toast.success(`${result.updated.length} ta narx import qilindi`);
      await queryClient.invalidateQueries({ queryKey: ['prices'] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.content.all('products') });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card>
      <CardHeader
        title="XLSX import / eksport"
        description="Narxlar SKU bo‘yicha moslashtiriladi"
      />
      <CardBody className="space-y-4">
        <Button
          variant="outline"
          icon={<Download className="size-4" />}
          loading={exportFile.isPending}
          onClick={() => exportFile.mutate()}
        >
          Amaldagi narxlarni yuklab olish
        </Button>

        <div className="space-y-2 border-t border-n-200 pt-4">
          <input
            ref={inputRef}
            type="file"
            hidden
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setPreview(null);
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              icon={<Upload className="size-4" />}
              onClick={() => inputRef.current?.click()}
            >
              Fayl tanlash
            </Button>
            <span className="text-sm text-n-600">{file?.name ?? 'Fayl tanlanmagan'}</span>
          </div>

          <Input
            label="Izoh"
            value={note}
            placeholder="Yangi narxnoma"
            onChange={(event) => setNote(event.target.value)}
          />

          {preview === null ? (
            <Button
              disabled={file === null}
              loading={importFile.isPending}
              onClick={() => importFile.mutate(true)}
            >
              Faylni tekshirish
            </Button>
          ) : (
            <div className="space-y-3 border border-warning-200 bg-warning-50 p-3">
              <p className="text-sm text-n-800">
                <strong>{preview.updated.length}</strong> ta narx yangilanadi
                {preview.unknownSkus.length > 0 && (
                  <span className="text-error-600">
                    {' '}
                    · {preview.unknownSkus.length} ta SKU topilmadi
                  </span>
                )}
                {preview.invalidRows.length > 0 && (
                  <span className="text-error-600">
                    {' '}
                    · {preview.invalidRows.length} qator o‘qilmadi
                  </span>
                )}
              </p>

              {preview.unknownSkus.length > 0 && (
                <p className="max-h-20 overflow-y-auto bg-white px-2 py-1 font-mono text-xs text-n-600">
                  {preview.unknownSkus.join(', ')}
                </p>
              )}

              <div className="flex gap-2">
                <Button
                  loading={importFile.isPending}
                  disabled={preview.updated.length === 0}
                  onClick={() => importFile.mutate(false)}
                >
                  Tasdiqlash
                </Button>
                <Button variant="outline" onClick={() => setPreview(null)}>
                  Bekor qilish
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// ─── Tarix ───────────────────────────────────────────────────────────────────

function HistoryCard() {
  const [page, setPage] = useState(1);
  const params = { page, limit: 20 };

  const history = useQuery({
    queryKey: ['prices', 'history', params],
    queryFn: () => pricesApi.history(params),
  });

  return (
    <Card>
      <CardHeader title="O‘zgarishlar tarixi" description="Kim, qachon, qaysi narxni o‘zgartirdi" />
      <CardBody>
        <DataTable
          rows={history.data?.data ?? []}
          rowKey={(row) => row._id}
          loading={history.isLoading}
          meta={history.data?.meta}
          onPageChange={setPage}
          emptyTitle="Hozircha o‘zgarish yo‘q"
          emptyHint="Narx o‘zgartirilganda har bir yozuv shu yerda qoladi."
          columns={[
            {
              key: 'createdAt',
              header: 'Sana',
              width: '150px',
              render: (row) => <span className="text-n-600">{dateTime(row.createdAt)}</span>,
            },
            {
              key: 'product',
              header: 'Mahsulot',
              render: (row) => (
                <span className="text-n-800">
                  {row.productTitle}
                  <span className="text-n-400"> · {row.variantTitle}</span>
                </span>
              ),
            },
            {
              key: 'sku',
              header: 'SKU',
              width: '140px',
              hideOnTablet: true,
              render: (row) => <code className="text-xs text-n-600">{row.sku ?? '—'}</code>,
            },
            {
              key: 'change',
              header: 'O‘zgarish',
              align: 'right',
              width: '230px',
              render: (row) => (
                <span className="text-n-800">
                  <span className="text-n-400">{formatPrice(row.oldPrice)}</span>
                  {' → '}
                  <span className="font-medium">{formatPrice(row.newPrice)}</span>
                </span>
              ),
            },
            {
              key: 'source',
              header: 'Manba',
              width: '120px',
              hideOnTablet: true,
              render: (row) => <Badge>{SOURCE_LABELS[row.source]}</Badge>,
            },
            {
              key: 'user',
              header: 'Kim',
              width: '180px',
              hideOnTablet: true,
              render: (row) => <span className="text-n-600">{row.userEmail || '—'}</span>,
            },
          ]}
        />
      </CardBody>
    </Card>
  );
}
